// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from '@imprivata-cloud/common';
import type { CodingContext, Secret } from '@imprivata-cloud/data-privacy-js';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import type { Patient } from '../../models/patient';
import { apiClient } from '../client';

const getPatientQueryKey = 'get-patient';

interface GetPatientResponse {
  id: string;
  givenName: Secret;
  surname: Secret;
  dateOfBirth: Secret;
  mrn: Secret;
  isAboveMinimumAge: boolean;
  photoUrl: string;
}

const getPatientQuery = async (codingContext: CodingContext | undefined): Promise<Patient> => {
  try {
    const response = await apiClient.get<GetPatientResponse>('patient', codingContext);
    return {
      id: response.id,
      givenName: codingContext?.decryptToString(response.givenName) ?? '',
      surname: codingContext?.decryptToString(response.surname) ?? '',
      dateOfBirth: codingContext?.decryptToString(response.dateOfBirth) ?? '',
      mrn: codingContext?.decryptToString(response.mrn) ?? '',
      isAboveMinimumAge: response.isAboveMinimumAge,
      photoUrl: response.photoUrl,
    };
  } catch (error: unknown) {
    const err = error as ApiErrorPayloadCommon;
    throw {
      cause: err.error.code ?? 'unknown',
      message: err.error.message ?? 'Unknown error',
      code: err.error.code ?? 'unknown',
      data: err.error.data,
    };
  }
};

export const useGetPatientQuery = (isEnabled = true) => {
  const { codingContext } = usePlatform();
  const [enabled, setEnabled] = useState(isEnabled);
  const query = useQuery<Patient, ApiErrorPayloadCommon & { cause?: string }>({
    queryKey: [getPatientQueryKey],
    queryFn: () => getPatientQuery(codingContext),
    enabled: enabled && !!codingContext,
  });

  return {
    patient: query.data,
    isLoading: query.isLoading,
    error: query.error,
    enable: () => setEnabled(true),
  };
};
